/**
 * Noticing that a single-page application changed page.
 *
 * There is no event for this. `popstate` fires for the back button and for
 * nothing else; a router calling `history.pushState` changes the URL in the
 * address bar and tells no one. So the two methods are wrapped, which is the
 * only technique available and is also how every other analytics script does
 * it — including, frequently, one that is already on the page. See
 * `neighbours.ts`: the fact that we were second is measured before we wrap.
 */

type Unwrap = () => void;

/**
 * Watch for URL changes and call `onChange` once per change, coalesced.
 *
 * ## Why the callback is scheduled instead of called
 *
 * A router does not perform one history operation per navigation. A guarded
 * route does `pushState('/dashboard')` and then, a line later,
 * `replaceState('/login')` when it finds no session — synchronously, in the
 * same tick. Called directly, this would report two page views, one of them for
 * a page nobody ever saw, and the dashboard would show a phantom `/dashboard`
 * on every anonymous visit. Scheduling on a macrotask and reading the URL when
 * it fires collapses that pair into the one navigation that actually happened.
 *
 * The cost is honest and small: a navigation followed within the same tick by
 * the page unloading is not reported. That is a user who clicked a link and
 * immediately left, and it is a far cheaper loss than a systematic phantom.
 *
 * ## Why the unwrap checks before it restores
 *
 * By the time we let go, a third script may have wrapped our wrapper. Putting
 * the original back then would delete that script's patch along with ours, and
 * we would have broken somebody else's tool from inside their customer's page.
 * If we are no longer the outermost wrapper, we leave the chain alone; ours
 * becomes a pass-through, which is inert.
 */
export function watchUrl(win: Window, onChange: () => void): Unwrap {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let stopped = false;

  const schedule = (): void => {
    if (stopped || timer !== undefined) return;
    timer = setTimeout(() => {
      timer = undefined;
      // The wrapped method is the customer's code path. An exception thrown
      // from here would surface as an exception thrown by their router.
      try {
        onChange();
      } catch {
        /* never into the host page */
      }
    }, 0);
  };

  const wrap = (name: 'pushState' | 'replaceState'): Unwrap => {
    const history = win.history;
    const original = history[name];
    if (typeof original !== 'function') return noop;
    const patched = function (this: History, ...args: unknown[]): unknown {
      // Call through FIRST and unconditionally. Whatever we do afterwards, the
      // router's own call must behave exactly as it would have without us —
      // including its return value and including when it throws, which is why
      // there is no try/catch around this line and one around everything else.
      const result = (original as (...a: unknown[]) => unknown).apply(this, args);
      schedule();
      return result;
    };
    (history as unknown as Record<string, unknown>)[name] = patched;
    return () => {
      if ((history as unknown as Record<string, unknown>)[name] === patched) {
        (history as unknown as Record<string, unknown>)[name] = original;
      }
    };
  };

  const unwrapPush = wrap('pushState');
  const unwrapReplace = wrap('replaceState');

  // The back and forward buttons. `hashchange` is deliberately absent — see
  // `core.ts` on why a hash route is not counted automatically.
  const onPop = (): void => schedule();
  win.addEventListener('popstate', onPop);

  return () => {
    stopped = true;
    if (timer !== undefined) clearTimeout(timer);
    win.removeEventListener('popstate', onPop);
    unwrapPush();
    unwrapReplace();
  };
}

function noop(): void {
  /* a history without pushState needs no unwrapping */
}
